import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import TableReadPlayer from './Pages/TableRead/TableReadPlayer';
import StartRead from './Pages/TableRead/StartRead';

/**
 * Table read as a full-height overlay on Blade pages (the analysis view). Same component as the
 * standalone page; it fills the overlay and owns its own scrolling.
 *
 *   window.SRTableRead.open({ base: '/table-read/<hash>', scene: 10, title: 'GO', shareId: '' })
 *
 * ms: start this far into the scene (global ms of its playlist). autoPlay: start playing at once — call
 * open() from inside the click handler: the player is rendered synchronously and its audio elements
 * are play()ed in the gesture, which is what lets Safari (iPhone) start sound once the clip arrives.
 *
 * shareId: the analysis page's ?share_id=, so listens through a shared link are attributed to it
 * (the server verifies it against the script before counting it as "shared_link").
 *
 * A plain overlay rather than a Bootstrap modal: it may be opened from INSIDE a scene modal, and
 * nesting Bootstrap modals fights over focus and the backdrop.
 */
let root = null, host = null, prevOverflow = '';

function close() {
  if (root) { root.unmount(); root = null; }
  if (host) { host.remove(); host = null; }
  document.body.style.overflow = prevOverflow;
}

function open({ base, scene = null, title = '', shareId = '', ms = null, autoPlay = false }) {
  close();
  // Opened from inside a scene modal: close it first. Bootstrap's focus trap would otherwise
  // pull focus back into the modal on every click in the overlay (selects, buttons).
  document.querySelectorAll('.modal.show').forEach((m) => {
    try { window.bootstrap?.Modal?.getInstance(m)?.hide(); } catch (_) {}
  });
  host = document.createElement('div');
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', 'Table read');
  Object.assign(host.style, { position: 'fixed', inset: '0', zIndex: '2147483000', background: '#fff' });
  document.body.appendChild(host);
  prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  root = createRoot(host);
  const el = <TableReadPlayer base={base} initialScene={scene} initialMs={ms} autoPlay={autoPlay} scriptTitle={title} shareId={shareId} onClose={close} />;
  if (!autoPlay) { root.render(el); return; }
  flushSync(() => root.render(el));
  // Unlock both audio elements while the click is still on the stack (they have no source yet, so
  // the play() itself fails; WebKit lifts the gesture restriction on the element anyway).
  host.querySelectorAll('audio').forEach((a) => { try { const p = a.play(); if (p && p.catch) p.catch(() => {}); } catch (_) {} });
}

// Idempotent: the analysis page may include this bundle from more than one partial.
if (!window.SRTableRead) {
window.SRTableRead = { open, close };

/**
 * "Hear this script" on an analysis with no read yet, and the live progress line that replaces the
 * button once recording starts. The page is Blade and does not reload, so the progress is polled
 * here and turns itself into the Listen button as soon as a scene is playable.
 */
function startSheet(btn, autoFull = false) {
  const base = btn.dataset.tablereadStart;
  const host2 = document.createElement('div');
  document.body.appendChild(host2);
  const r2 = createRoot(host2);
  const shut = () => { r2.unmount(); host2.remove(); };
  r2.render(<div className="tr-shell" style={{ position: 'fixed', inset: 0, height: '100%', background: 'transparent', zIndex: 2147483000 }}>
    <StartRead base={base} title={btn.dataset.tablereadTitle || ''} onClose={shut} autoFull={autoFull}
               onListen={() => { shut(); showProgress(btn, base); open({ base, title: btn.dataset.tablereadTitle || '' }); }}
               onStarted={() => { shut(); showProgress(btn, base); }} />
  </div>);
}

const PROGRESS_CSS = `
.tr-page-progress{display:inline-flex;align-items:center;gap:10px;font-size:.9rem;color:#14536e}
.tr-page-bar{display:inline-block;width:120px;height:6px;border-radius:3px;background:#e3edf1;overflow:hidden}
.tr-page-bar > i{display:block;height:100%;width:0;background:#14536e;border-radius:3px;transition:width .4s ease}
.tr-page-wrap{display:inline-flex;align-items:center;gap:8px;flex-wrap:wrap}
.tr-page-note{font-size:.8rem;color:#5b7f8d;white-space:nowrap}
`;

/**
 * The live line on the analysis page while a read records: a bar until something is playable, then
 * the real Listen button with a small "N of M recorded" note beside it until the read is done.
 * Polling stops on completion, on an expired/absent read, and after 40 minutes (a dead coordinator
 * must not leave a tab polling forever).
 */
function showProgress(btn, base) {
  if (!document.getElementById('tr-page-css')) {
    const st = document.createElement('style'); st.id = 'tr-page-css'; st.textContent = PROGRESS_CSS;
    document.head.appendChild(st);
  }
  const box = document.createElement('span');
  box.className = 'tr-page-progress';
  box.innerHTML = '<span class="tr-page-bar"><i></i></span><span class="tr-page-text">Recording your table read…</span>';
  btn.replaceWith(box);
  const text = box.querySelector('.tr-page-text');
  const bar = box.querySelector('.tr-page-bar > i');
  const title = btn.dataset.tablereadTitle || '';
  const until = Date.now() + 40 * 60 * 1000;
  let note = null;                                   // set once the button has taken the box's place

  const tick = () => {
    fetch(`${base}/scenes`, { credentials: 'same-origin' }).then((r) => r.json()).then((d) => {
      const p = d.progress || {};
      const ready = p.ready || 0, total = p.total || 0;
      // No progress block at all = the generation has not been created yet (start is a background
      // process) or this is a legacy read: keep watching, it is not a finished state.
      const building = d.progress ? !!p.building : true;

      if (ready > 0 && !note) {
        // Playable: become the real button, with the count beside it. Everything else (paying for
        // more scenes) lives inside the player.
        const wrap = document.createElement('span');
        wrap.className = 'tr-page-wrap';
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn btn-primary btn-sm shadow-sm d-inline-flex align-items-center gap-1 px-3 py-1';
        b.style.fontWeight = '600';
        b.innerHTML = '<i class="fa fa-headphones"></i> Listen to the Table Read';
        b.dataset.tablereadOpen = base;
        b.dataset.tablereadScene = String((d.scenes || []).find((s) => s.rendered)?.number ?? 1);
        b.dataset.tablereadTitle = title;
        note = document.createElement('span');
        note.className = 'tr-page-note';
        wrap.append(b, note);
        box.replaceWith(wrap);
      }
      if (note) {
        note.textContent = building && total
          ? `${ready} of ${total} scenes recorded · about ${Math.max(1, Math.round((total - ready) * 0.45))} min left` : '';
      } else {
        if (total) bar.style.width = `${Math.round((ready / total) * 100)}%`;
        text.textContent = building
          ? 'Recording your table read — the first scenes take about 3–5 minutes'
          : 'The table read could not be recorded. Try again later.';
      }
      if (!building || Date.now() > until) return;   // done, or stop watching a read that stalled
      setTimeout(tick, 15000);
    }).catch(() => { if (Date.now() < until) setTimeout(tick, 30000); });
  };
  tick();
}

/**
 * Coming back from a credit purchase (?tableread=<hash>&trscene=N): re-open the read where they
 * were, instead of dropping them on the analysis page. The parameters are removed from the URL so a
 * refresh does not keep re-opening it.
 */
function resumeFromUrl() {
  const q = new URLSearchParams(window.location.search);
  // Back from a credit top-up made in the START dialog (no read exists yet): open that dialog again.
  const startHash = q.get('trstart');
  if (startHash) {
    const sb = [...document.querySelectorAll('[data-tableread-start]')]
      .find((b) => (b.dataset.tablereadStart || '').endsWith('/' + startHash));
    const auto = q.get('trfull') === '1';
    q.delete('trstart'); q.delete('trfull');
    const su = window.location.pathname + (q.toString() ? '?' + q : '') + window.location.hash;
    try { history.replaceState(null, '', su); } catch (_) {}
    if (sb) startSheet(sb, auto);
    return;
  }
  const hash = q.get('tableread');
  if (!hash) return;
  const btn = [...document.querySelectorAll('[data-tableread-open]')]
    .find((b) => (b.dataset.tablereadOpen || '').endsWith('/' + hash));
  q.delete('tableread'); const scene = q.get('trscene'); q.delete('trscene');
  const url = window.location.pathname + (q.toString() ? '?' + q : '') + window.location.hash;
  try { history.replaceState(null, '', url); } catch (_) {}
  if (!btn) return;
  open({ base: btn.dataset.tablereadOpen, scene: scene ? +scene : null,
         title: btn.dataset.tablereadTitle || '', shareId: q.get('share_id') || '' });
}
// A read that was already recording when the page loaded gets the same live line.
function watchBuilding() {
  document.querySelectorAll('[data-tableread-building]').forEach((el) => showProgress(el, el.dataset.tablereadBuilding));
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { resumeFromUrl(); watchBuilding(); });
else { resumeFromUrl(); watchBuilding(); }

document.addEventListener('click', (e) => {
  const s = e.target.closest('[data-tableread-start]');
  if (s) { e.preventDefault(); startSheet(s); return; }
  const b = e.target.closest('[data-tableread-open]');
  if (!b) return;
  e.preventDefault();
  // The share id is read from the page URL, so every entry point (top, scenes, scene modal) carries it.
  const shareId = new URLSearchParams(window.location.search).get('share_id') || '';
  open({ base: b.dataset.tablereadOpen, scene: b.dataset.tablereadScene ? +b.dataset.tablereadScene : null,
         title: b.dataset.tablereadTitle || '', shareId });
});
}
